import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  ExclusionResponse,
  ExclusionType,
  TakeABreakFromUsInformation,
  useExclusionType,
  useSelfExclusionData
} from '@swf/account';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import { select } from '@swf/core/store';
import { useFormattedSelfExclusionDate } from '@swf/core/utils/selfExclusionDate';
import AccountVerification from '@swf/verification/AccountVerification/AccountVerification';

import type { HelpSection } from '../../../../types/Cashier';
import { PaySystemErrorCode } from '../../../../types/status';
import AMLInfoMessage from '../../../AMLInfoMessage/AMLInfoMessage';
import AMLSuspended from '../../../AMLSuspended/AMLSuspended';
import CTAButton from '../../../CTAButton/CTAButton';
import Container from '../../../Container/Container';
import NationalExclusion, {
  shouldShowNationalExclusion
} from '../../../NationalExclusion/NationalExclusion';
import SelfExcludedHelpSectionContent from '../../../P2pTransfer/components/P2pSelfExcluded/HelpSectionContent/HelpSectionContent';
import { isDenmarkCitizen } from '../../../RMF/utils/rmf';
import SuspendRM from '../../../Withdraw/components/SuspendRM/SuspendRM';
import HelpSectionContent from '../../../Withdraw/components/WithdrawRestriction/components/HelpSectionContent/HelpSectionContent';
import HelpSectionAccountVerification from '../../../Withdraw/components/WithdrawRestriction/components/HelpSectionVerification/HelpSectionVerification';
import DepositAccountVerificationCSForm from '../DepositAccountVerificationCSForm/DepositAccountVerificationCSForm';
import DepositAccountVerificationHelpContent from '../DepositAccountVerificationCSForm/HelpSectionContent/HelpSectionContent';
import DepositLugasBlocked from '../DepositLugasBlocked/DepositLugasBlocked';
import DepositRestricted from '../DepositRestricted/DepositRestricted';
import RestrictedDepositHelpSectionContent from '../DepositRestricted/components/HelpSectionContent';
import DepositRmfRequired from '../DepositRmfRequired/DepositRmfRequired';

import {
  TXTCLI_Deposit,
  TXTCLI_Verify_account,
  TXTWCL_About_deposit_limits,
  TXTWCL_Back_to_Cashier,
  TXTWCL_Help_with_account_verification,
  TXTWCL_Help_with_deposits,
  TXTWCL_Help_with_national_self_exclus_ELL,
  TXTWCL_Help_with_self_exclusion,
  TXTWCL_Help_with_verification,
  TXTWCL_Select_which_document_youXd_li_ELL,
  TXTWCL_This_means_you_wonXt_be_able_t_ELL_18,
  TXTWCL_To_continue_using_your_account_ELL_1,
  TXTWCL_We_need_to_verify_your_identit_ELL_1,
  TXTWCL_YouXre_self_excludedX,
  TXTWCL_YouXre_self_excluded_Nationall_ELL,
  TXTWCL_YouXre_taking_a_break,
  TXTWCL_You_canXt_deposit_right_now
} from './DepositSuspended.messages.json';
import { page, title } from './DepositSuspended.scss';
import HelpSectionNationalExclusion from './components/HelpSectionNationalExclusion/HelpSectionNationalExclusion';

const getHelpSection = (
  depositRestricted: boolean,
  isTakingABreak: boolean,
  noPaysystemsAvailableError: boolean,
  showRmf: boolean,
  isSelfExcluded: boolean,
  showAccountSuspendedHelp: boolean,
  showAccountVerificationHelp: boolean,
  isSwcLugasMessageEnabled: boolean,
  nationalExclusion: boolean
): HelpSection | undefined => {
  if (nationalExclusion) {
    return {
      title: TXTWCL_Help_with_national_self_exclus_ELL,
      body: <HelpSectionNationalExclusion />
    };
  }
  if (showAccountVerificationHelp) {
    return {
      title: TXTWCL_Help_with_verification,
      body: <HelpSectionAccountVerification />
    };
  }
  if (showAccountSuspendedHelp) {
    return {
      title: TXTWCL_Help_with_account_verification,
      body: <DepositAccountVerificationHelpContent />
    };
  }
  if (depositRestricted && !isTakingABreak) {
    return {
      title: TXTWCL_About_deposit_limits,
      body: <RestrictedDepositHelpSectionContent />
    };
  }
  if (isSelfExcluded || isTakingABreak) {
    return {
      title: TXTWCL_Help_with_self_exclusion,
      body: <SelfExcludedHelpSectionContent />
    };
  }
  if ((!noPaysystemsAvailableError && !showRmf) || isSwcLugasMessageEnabled) {
    return {
      title: TXTWCL_Help_with_deposits,
      body: <HelpSectionContent />
    };
  }
  return;
};

const DepositSuspended: React.FC = () => {
  const exitWizard = useWizardExit();
  const amlSuspended = useSelector(select.account.amlSuspended);
  const nationalExclusion = useSelector(select.account.bannedByRegulator);
  const isSuspendedRM = useSelector(select.account.isSuspendRM);
  const citizenship = useSelector(select.account.getUserCitizenship);
  const denmarkCitizen = isDenmarkCitizen(citizenship || '');
  const { selfExclusionData, selfExcludedDataFetched } = useSelfExclusionData();
  const exclusionType = useExclusionType(selfExclusionData?.daysRequested, nationalExclusion);
  const isTakingABreak = exclusionType === ExclusionType.Break;
  const isSelfExcluded = exclusionType === ExclusionType.SelfExclusion;
  const currentlyTakingBreak = isSelfExcluded || isTakingABreak;
  const { timeUntil, daysRequested } = selfExclusionData || {};
  const site = useSelector(select.cms.getSiteId);
  const isSiteDK = site === Sites.DEN;
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const isRealMoneyOK = useSelector(select.account.isRealMoneyOK);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const IDScanSDK = useFeature(FeatureFlag.IDScanSDK);
  const verifyDoc = useFeature(FeatureFlag.VerifyDoc);
  const showAccountSuspendedHelp = !verifyDoc && amlSuspended;
  const showAccountVerification = verifyDoc && amlSuspended;

  const showNationalExclusion = shouldShowNationalExclusion(site);

  const {
    data: { cmsHeaderAndGridLayout, depositRestricted },
    error: wizardError
  } = useWizardContext();

  const { globalErrors: [error] = [] } = wizardError || {};
  const noPaysystemsAvailableError = error?.code === PaySystemErrorCode.PaySystemsNotAvailable;
  const isDepositRestricted = depositRestricted || noPaysystemsAvailableError;
  const isManageDepositLimitsEnabled = useFeature(FeatureFlag.SwcManageDepositLimits);
  const isSwcLugasMessageEnabled = useFeature(FeatureFlag.SwcLugasMessage);

  const showRmf = (isSiteUS && !isRealMoneyOK) || (isSiteDK && !isRealMoneyOK && denmarkCitizen);
  const formattedDate = useFormattedSelfExclusionDate();

  const renderContent = () => {
    switch (true) {
      case showRmf:
        return <DepositRmfRequired />;
      case isSuspendedRM:
        return <SuspendRM />;
      case isSelfExcluded || isTakingABreak:
        return (
          <div>
            {isTakingABreak && (
              <>
                <Translation {...TXTWCL_YouXre_taking_a_break} />
                {'. '}
              </>
            )}
            {isSelfExcluded && (
              <>
                <Translation {...TXTWCL_YouXre_self_excludedX} />{' '}
              </>
            )}
            <Translation
              {...TXTWCL_This_means_you_wonXt_be_able_t_ELL_18}
              values={{ 0: <b>{formattedDate}</b> }}
            />{' '}
            <CTAButton type="button" alias="firstWalletHome" translation={TXTWCL_Back_to_Cashier} />
          </div>
        );
      case nationalExclusion:
        return (
          <div>
            {showNationalExclusion ? (
              <>
                <NationalExclusion />
                <CTAButton
                  type="button"
                  alias="firstWalletHome"
                  translation={TXTWCL_Back_to_Cashier}
                />
              </>
            ) : (
              <ExclusionResponse timeUntil={timeUntil} daysRequested={daysRequested} />
            )}
            {!showNationalExclusion && <TakeABreakFromUsInformation />}{' '}
          </div>
        );
      case isDepositRestricted:
        return (
          <DepositRestricted
            wizardError={noPaysystemsAvailableError}
            errorMessage={error?.defaultMessage ?? ''}
          />
        );
      case isSwcLugasMessageEnabled:
        return <DepositLugasBlocked />;
      case !IDScanSDK && !verifyDoc:
        return <DepositAccountVerificationCSForm />;
      default:
        return (
          <AccountVerification
            onClose={exitWizard}
            onComplete={exitWizard}
            showButton
            rowLayout
            infoMessage={<AMLInfoMessage />}
            title={TXTWCL_Select_which_document_youXd_li_ELL}
            titleClassName={title}
          >
            {amlSuspended && <AMLSuspended />}
          </AccountVerification>
        );
    }
  };

  return (
    <Container
      page={DepositSuspended}
      headerProps={{
        title:
          isSelfExcluded || isTakingABreak ? (
            <Translation {...TXTWCL_You_canXt_deposit_right_now} />
          ) : (
            <Translation {...(isDepositRestricted ? TXTCLI_Deposit : TXTCLI_Verify_account)} />
          ),
        buttons: { back: true, exitWizardOnBackClick: true }
      }}
      formClassName={page}
      pageClassName={page}
      helpSection={getHelpSection(
        depositRestricted,
        isTakingABreak,
        noPaysystemsAvailableError,
        showRmf,
        isSelfExcluded,
        showAccountSuspendedHelp,
        showAccountVerification,
        isSwcLugasMessageEnabled,
        nationalExclusion
      )}
      {...(isMobileEmbedded && { limitsSection: isManageDepositLimitsEnabled })}
      helpSupportResolver={ResolverTag.HelpSelfExcludedAccount}
      showFooter
      {...(cmsHeaderAndGridLayout && {
        pageTopTitle:
          (nationalExclusion && <Translation {...TXTWCL_YouXre_self_excluded_Nationall_ELL} />) ||
          (currentlyTakingBreak && <Translation {...TXTWCL_You_canXt_deposit_right_now} />)
      })}
      {...(showAccountVerification &&
        !isDepositRestricted && {
          pageTopTitle: <Translation {...TXTWCL_We_need_to_verify_your_identit_ELL_1} />,
          pageTopSubtitle: <Translation {...TXTWCL_To_continue_using_your_account_ELL_1} />
        })}
      {...(depositRestricted && isManageDepositLimitsEnabled && { limitsSection: true })}
    >
      {selfExcludedDataFetched && renderContent()}
    </Container>
  );
};

DepositSuspended.displayName = 'DepositSuspended';

export default DepositSuspended;
